//libraries
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import DOMPurify from "dompurify";

// Components

import ShowMoreModal from "../../../common/components/ShowMoreModal";
import ReportViewModal from "../../../common/components/ReportViewModal";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import ViewAttachmentsModal from "../../../common/components/ViewAttachmentsModal";

// Helpers
import { getStatusConfig } from "../helpers/upcomingReports.helper";
import { getFullName } from "../../../reports-settings/report-type/helpers/form.handler";
import { all_routes } from "../../../../router/all_routes";
import { BackendAllReport } from "../../../common/types/report";
import { exportReportToPDF } from "../../../common/helpers";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";

const AllReportsCard: React.FC<{
  report: BackendAllReport;
  onDeleteReport?: any;
  onCreateTemplate?: any;
  allUsers?: any;
  upcomingReportType?: any[];
}> = ({ report, onDeleteReport, onCreateTemplate, allUsers, upcomingReportType }) => {
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [reportIdForTemplate, setReportIdForTemplate] = useState<any>(null);
  const [selectedPreviewReport, setSelectedPreviewReport] = useState<any>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState(false);
  const [isReportViewModalOpen, setIsReportViewModalOpen] = useState(false);
  const [isViewAttachmentsModalOpen, setIsViewAttachmentsModalOpen] =
    useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  const handleCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setIsShowMoreModalOpen(false);
    setIsReportViewModalOpen(false);
    setIsViewAttachmentsModalOpen(false);
    setIsTemplateModalOpen(false);
  }, []);
  const handleDeleteModalOpen = useCallback((report_id: any) => {
    setDeleteItem(report_id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleShowMore = useCallback(() => {
    setIsShowMoreModalOpen(true);
  }, []);
  const handleViewReport = (report: any) => {
    setSelectedPreviewReport(report);
    setIsReportViewModalOpen(true);
  };

  const handleViewAttachments = (attachments: any) => {
    setSelectedAttachments(attachments);
    setIsViewAttachmentsModalOpen(true);
  };

  const handleCreateTemplate = (report_id: any) => {
    setReportIdForTemplate(report_id);
    setIsTemplateModalOpen(true);
  };

  // Convert backend status to frontend format for helper functions
  const frontendStatus = report?.status_text?.toLowerCase() as
    | "completed"
    | "pending"
    | "draft";
  const statusConfig = getStatusConfig(frontendStatus);

  const handleDelete = () => {
    onDeleteReport(deleteItem);
  };

  const handleCreateTemplateApi = () => {
    onCreateTemplate(reportIdForTemplate);
  };

  const showMaxItems = 1;
  const displayNames = report?.visible_to.slice(0, showMaxItems);
  const hasMore = report?.visible_to.length > showMaxItems;
  const modalDisplayNames = report?.visible_to.slice(showMaxItems);

  // Format dates from backend
  const createdDate = humanReadableDate(
    report?.submitted_at || report?.created_at,
    true
  );
  const reportDate = humanReadableDate(
    report?.report_date,
    false
  );

  // Enhanced content preview for TipTap HTML content
  const createRichContentPreview = (
    htmlContent: string
  ): { preview: string; hasMore: boolean } => {
    if (!htmlContent)
      return { preview: "No content available", hasMore: false };

    // Sanitize the HTML content
    const sanitizedContent = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
      ],
      ALLOWED_ATTR: [],
    });

    // Create preview by limiting content length
    const maxChars = 200;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitizedContent;

    const fullText = tempDiv.textContent || tempDiv.innerText || "";
    const hasMore = fullText.length > maxChars;

    if (!hasMore) {
      return { preview: sanitizedContent, hasMore: false };
    }

    // Truncate HTML content while preserving structure
    let truncatedHtml = "";
    let charCount = 0;
    let shouldBreak = false;

    const walkNodes = (node: Node) => {
      if (shouldBreak) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent || "";
        if (charCount + textContent.length > maxChars) {
          const remainingChars = maxChars - charCount;
          truncatedHtml += textContent.substring(0, remainingChars) + "...";
          shouldBreak = true;
        } else {
          truncatedHtml += textContent;
          charCount += textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        truncatedHtml += `<${element.tagName.toLowerCase()}>`;

        for (let child of Array.from(node.childNodes)) {
          walkNodes(child);
          if (shouldBreak) break;
        }

        if (!shouldBreak) {
          truncatedHtml += `</${element.tagName.toLowerCase()}>`;
        }
      }
    };

    // Parse and walk the DOM tree
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedContent, "text/html");

    for (let child of Array.from(doc.body.childNodes)) {
      walkNodes(child);
      if (shouldBreak) break;
    }

    return {
      preview: DOMPurify.sanitize(truncatedHtml),
      hasMore: true,
    };
  };

  const { preview: contentPreview, hasMore: hasMoreContent } =
    createRichContentPreview(report.report_content);

  const getFrequencyDisplayText = (): string => {
    const { frequency_type, weekly_interval, monthly_interval } = report;
    if (!frequency_type) {
      return "Custom";
    }

    switch (frequency_type) {
      case "daily":
        return "Daily";
      case "weekly":
        return weekly_interval === 1
          ? "Weekly"
          : `Every ${weekly_interval} weeks`;
      case "monthly":
        return monthly_interval === 1
          ? "Monthly"
          : `Every ${monthly_interval} months`;
      default:
        return frequency_type.charAt(0).toUpperCase() + frequency_type.slice(1);
    }
  };

  return (
    <>
      <div className="card report-card border-0 mb-4 shadow-sm">
        <div className="card-body p-3 bg-white">
          <div className="row align-items-center">
            {/* Report Info Section */}
            <div className="col-lg-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i
                    className={`ti ti-file-analytics ${statusConfig.iconColor} fs-24`}></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="report-name mb-2 text-dark">
                    {report.report_title}
                  </h6>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span
                      className={`badge ${statusConfig.bgClass} ${statusConfig.textClass} badge-xs`}>
                      {report.status_text}
                    </span>
                    <small className="badge badge-sm badge-secondary-transparent d-flex align-items-center">
                      <i className="ti ti-repeat me-1"></i>
                      {getFrequencyDisplayText()}
                    </small>
                    <small
                      className={`badge badge-sm badge-${report.is_submitted_late ? "danger" : "success"
                        } d-flex align-items-center`}>
                      {report.is_submitted_late ? "Late" : "On Time"}
                    </small>
                  </div>
                  {report?.report_type_id != -1 && (
                    <small className="text-primary d-block mb-2">
                      <i className="ti ti-calendar me-1"></i>
                      Report Date: {reportDate}
                    </small>
                  )}
                  <small className="text-muted d-block mb-2">
                    <i className="ti ti-calendar me-1"></i>
                    Created: {createdDate}
                  </small>

                  <small className="text-primary d-block mb-2">
                    {!report.can_edit ? (
                      <i className="ti ti-edit-off me-1"></i>
                    ) : (
                      <i className="ti ti-edit me-1"></i>
                    )}

                    {report?.is_draft || report?.report_type_id == -1 ? (
                      report?.edit_reason
                    ) : (
                      <>
                        {report?.can_edit
                          ? `Can Edit Until ${humanReadableDate(
                            report?.edit_deadline
                          )}`
                          : report?.edit_reason}
                      </>
                    )}
                  </small>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              {/* Rich Content Preview */}
              <div className="content-preview-section mb-3">
                <div
                  className="content-preview-body"
                  style={{
                    maxHeight: "80px",
                    overflow: "hidden",
                    position: "relative",
                  }}>
                  {/* Render sanitized HTML with custom styles */}
                  <div
                    dangerouslySetInnerHTML={{ __html: contentPreview }}
                    style={{
                      fontSize: "0.8rem",
                      lineHeight: "1.3",
                      color: "#6c757d",
                    }}
                    className="tiptap-preview"
                  />

                  {/* Fade overlay for long content */}
                  {hasMoreContent && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "20px",
                        background: "linear-gradient(transparent, white)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex align-items-center justify-content-center">
                <div className="d-flex align-items-center justify-content-start gap-2 w-100">
                  <button
                    className="btn btn-info btn-sm px-2 py-1"
                    title="View Full Report"
                    onClick={() => handleViewReport(report)}>
                    <small className="d-flex align-items-center justify-content-center">
                      <i className="ti ti-eye me-1"></i>
                      View Full Report
                    </small>
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm px-2 py-1"
                    onClick={() => handleViewAttachments(report.attachments)}
                    title="View Attachments"
                    disabled={report.attachments_count === 0}>
                    <small className="d-flex align-items-center justify-content-center">
                      <i className="ti ti-paperclip me-1"></i>
                      {report.attachments_count} Attachment
                      {report.attachments_count !== 1 ? "s" : ""}
                    </small>
                  </button>
                </div>
              </div>
            </div>

            {/* Visible To*/}
            <div className="col-lg-2 ">
              <div className="assignment-section">
                <div className="badge badge-secondary-transparent d-flex align-items-center mb-2">
                  <i className={`ti ti-user text-secondary me-2 fs-18`}></i>
                  <h6 className="text-dark mb-0">Visible To</h6>
                </div>

                <div className="assignment-list">
                  {report.visible_to.length == 0 ? (
                    <small className="text-primary px-2">
                      No records to show!
                    </small>
                  ) : (
                    <>
                      {displayNames.map((item: any) => (
                        <div
                          key={item?.id}
                          className="px-1 assignment-item mb-1 d-flex align-items-center">
                          <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
                            <ImageWithBasePath
                              src={
                                !item?.profile_pic ||
                                  item?.profile_pic === "null"
                                  ? "assets/img/users/user.png"
                                  : item?.profile_pic
                              }
                              alt="img"
                            />
                          </li>

                          <span className={`text-muted me-1`}>
                            {getFullName(
                              item?.salutation,
                              item?.fname,
                              item?.lname
                            )}
                          </span>
                        </div>
                      ))}
                      {hasMore && (
                        <Link
                          to="#"
                          className="px-2 btn-link text-decoration-none"
                          onClick={(e: any) => {
                            e.preventDefault();
                            handleShowMore();
                          }}>
                          <small className="text-primary">
                            +{report?.visible_to.length - showMaxItems} more
                          </small>
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="col-lg-2 d-flex justify-content-center">
              <div className="report-actions gap-1 w-75">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleCreateTemplate(report.id)}
                  title="Download Report">
                  <i className="ti template me-1"></i>
                  Make Template
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => exportReportToPDF(report)}
                  title="Download Report">
                  <i className="ti ti-download me-1"></i>
                  Download
                </button>

                {report.can_edit ? (
                  <>
                    <Link
                      className="btn btn-outline-info btn-sm"
                      to={report.can_edit ? all_routes.ersReportsEdit : "#"}
                      state={{
                        reportData: report,
                        allUsers: allUsers,
                        upcomingReportType: upcomingReportType,
                      }}
                      title={"Edit Report"}>
                      <i className="ti ti-edit me-1"></i>
                      Edit
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      key={report.id}
                      className="btn btn-outline-info btn-sm"
                      disabled={true}
                      title="Cannot Edit">
                      <i className="ti ti-edit-off me-1"></i>
                      Edit
                    </button>
                  </>
                )}

                <button
                  key={report.id}
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDeleteModalOpen(report.id)}
                  disabled={!report.can_edit}>
                  {!report.can_edit ? (
                    <i className="ti ti-trash-off me-1"></i>
                  ) : (
                    <i className="ti ti-trash-x me-1"></i>
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        message="Are you sure you want to delete this item? This action cannot be undone."
        title="Delete Item"
        onConfirm={handleDelete}
        type="danger"
      />

      <ShowMoreModal
        isOpen={isShowMoreModalOpen}
        onClose={handleCloseModal}
        items={modalDisplayNames}
      />

      <ReportViewModal
        isOpen={isReportViewModalOpen}
        onClose={handleCloseModal}
        report={selectedPreviewReport}
      />

      <ViewAttachmentsModal
        isOpen={isViewAttachmentsModalOpen}
        onClose={handleCloseModal}
        attachments={selectedAttachments}
      />

      <ConfirmationModal
        isOpen={isTemplateModalOpen}
        onClose={handleCloseModal}
        title="Create Template"
        onConfirm={handleCreateTemplateApi}
        type="success"
      />
    </>
  );
};

export default AllReportsCard;
